import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, AlertStats } from '../types';
import { alertService } from '../services/api';
import { colors, shadows, borderRadius } from '../theme/designSystem';

const AlertPanel: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<AlertStats | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      const [alertsData, statsData] = await Promise.all([
        alertService.getRecent(5),
        alertService.getStats(),
      ]);
      setAlerts(alertsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await alertService.acknowledge(id);
      loadAlerts();
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return colors.status.error;
      case 'high':
        return colors.status.warning;
      case 'medium':
        return colors.status.info;
      case 'low':
        return colors.neutral[500];
      default:
        return colors.neutral[500];
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon sx={{ fontSize: '1rem', color: '#fff' }} />;
      case 'high':
        return <WarningIcon sx={{ fontSize: '1rem', color: '#fff' }} />;
      case 'medium':
        return <InfoIcon sx={{ fontSize: '1rem', color: '#fff' }} />;
      default:
        return <InfoIcon sx={{ fontSize: '1rem', color: '#fff' }} />;
    }
  };

  if (!stats || stats.unacknowledged === 0) {
    return null;
  }

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 3, 
        borderRadius: borderRadius.card, 
        boxShadow: shadows.card,
        borderLeft: `4px solid ${colors.status.warning}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: colors.status.warning }} />
          <Typography sx={{ fontWeight: 600, color: colors.neutral[900] }}>
            Active Alerts ({stats.unacknowledged})
          </Typography>
        </Box>
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <List sx={{ mt: 1 }}>
          {alerts.filter(a => !a.acknowledged).map((alert) => (
            <ListItem
              key={alert.id}
              sx={{
                backgroundColor: colors.neutral[50],
                borderRadius: borderRadius.sm,
                mb: 1,
                '&:last-child': { mb: 0 },
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcknowledge(alert.id);
                  }}
                  sx={{ color: colors.status.success }}
                >
                  <CheckCircleIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      icon={getSeverityIcon(alert.severity)}
                      label={alert.severity.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: getSeverityColor(alert.severity),
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {alert.title}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography sx={{ fontSize: '0.8125rem', color: colors.neutral[600] }}>
                    {alert.message}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default AlertPanel;
